import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { JOB_POSITIONS, JOB_CONTRACTS, PAYMENT_METHODS } from "../../constants";
import { useSelector } from "react-redux";

import api from "../../services/api";
import SelectUser from "../selectUser";
import { hasAccess } from "../../utils/roles";
import { capture } from "@/sentry";

const About = ({ user, setUser }) => {
  const sessionUser = useSelector((state) => state.Auth.user);
  const [values, setValues] = useState(user);
  const [businessUnits, setBusinessUnits] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data, ok } = await api.get("/business_unit");
        if (ok && data) setBusinessUnits(data);
      } catch (e) {
        console.error("Failed to fetch business units", e);
      }
    })();
  }, []);

  const handleDelete = async () => {
    const confirm = window.confirm("Are you sure ?");
    if (!confirm) return;
    try {
      await api.remove(`/user/${user._id}`);
      toast.success("successfully removed!");
      setUser(null);
    } catch (error) {}
  };

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      const newUser = await api.put(`/user/${user._id}`, values);
      if (!newUser.ok) return toast.error(newUser.code || "Failed to save");
      setUser(newUser.data);
      toast.success("Updated!");
    } catch (error) {
      capture(error, { values, user });
      toast.error("Some Error!");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 space-y-5">
      <PersonalDetails values={values} setValues={setValues} />
      {hasAccess({ user: sessionUser, access: "admin" }) && (
        <>
          <EmploymentDetails values={values} setValues={setValues} />
          <OrganisationDetails values={values} setValues={setValues} businessUnits={businessUnits} />
          <DatesDetails values={values} setValues={setValues} />
          <AdminDetails values={values} setValues={setValues} />
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" className="red-btn" onClick={handleDelete}>
              Delete
            </button>
            <button className="blue-btn" onClick={handleSubmit}>
              Update
            </button>
          </div>
        </>
      )}
    </form>
  );
};

// ─── UI Primitives ──────────────────────────────────────────────────────────

const SectionCard = ({ title, description, children }) => (
  <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
    <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/60">
      <h2 className="text-sm font-semibold text-gray-700">{title}</h2>
      {description && <p className="text-xs text-gray-400 mt-0.5">{description}</p>}
    </div>
    <div className="px-5 py-4">{children}</div>
  </div>
);

const Field = ({ label, hint, children, className = "" }) => (
  <div className={`space-y-1 w-full ${className}`}>
    <label className="block text-[11px] font-medium text-gray-400 uppercase tracking-wider">{label}</label>
    {children}
    {hint && <p className="text-[10px] text-gray-400 leading-tight">{hint}</p>}
  </div>
);

// ─── 1. Personal Information ────────────────────────────────────────────────

const PersonalDetails = ({ values, setValues }) => {
  return (
    <SectionCard title="Personal information" description="Identity, bio and external links">
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Name">
            <input className="input" type="text" name="name" value={values.name} onChange={(e) => setValues({ ...values, name: e.target.value })} />
          </Field>
          <Field label="Email">
            <input className="input" type="text" name="email" value={values.email} onChange={(e) => setValues({ ...values, email: e.target.value })} />
          </Field>
          <Field label="Description" className="md:col-span-2">
            <textarea
              rows={2}
              className="input"
              name="description"
              value={values.description}
              onChange={(e) => setValues({ ...values, description: e.target.value })}
              placeholder="Short bio..."
            />
          </Field>
        </div>

        <div className="border-t border-gray-100 pt-4">
          <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-2">Links</p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Field label="Github">
              <input className="input" type="text" name="github" value={values.github} onChange={(e) => setValues({ ...values, github: e.target.value })} placeholder="https://github.com/..." />
            </Field>
            <Field label="Calendly">
              <input
                className="input"
                type="text"
                name="calendly"
                value={values.calendly}
                onChange={(e) => setValues({ ...values, calendly: e.target.value })}
                placeholder="https://calendly.com/..."
              />
            </Field>
            <Field label="Linkedin">
              <input
                className="input"
                type="text"
                name="linkedin"
                value={values.linkedin}
                onChange={(e) => setValues({ ...values, linkedin: e.target.value })}
                placeholder="https://linkedin.com/in/..."
              />
            </Field>
            <Field label="Meeting">
              <input className="input" type="text" name="meeting" value={values.meeting} onChange={(e) => setValues({ ...values, meeting: e.target.value })} placeholder="Meeting link" />
            </Field>
            <Field label="Slack ID" hint="Slack user ID (e.g. U03V5NCD3TK) — used to @mention this person in Slack reminders">
              <input className="input" type="text" name="slack_id" value={values.slack_id || ""} onChange={(e) => setValues({ ...values, slack_id: e.target.value })} />
            </Field>
          </div>
        </div>
      </div>
    </SectionCard>
  );
};

// ─── 2. Employment Details ──────────────────────────────────────────────────

const EmploymentDetails = ({ values, setValues }) => {
  return (
    <SectionCard title="Employment" description="Contract type, position, role and seniority level">
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Contract" hint="Type of contract (CDI, CDD, Freelance...)">
            <select className="input" name="contract" value={values.contract} onChange={(e) => setValues({ ...values, contract: e.target.value })}>
              <option value=""></option>
              {JOB_CONTRACTS.map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Position" hint="Main discipline / role type">
            <select className="input" name="position" value={values.position} onChange={(e) => setValues({ ...values, position: e.target.value })}>
              <option value=""></option>
              {JOB_POSITIONS.map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Job title" hint="Displayed title (e.g. Senior Fullstack Dev)">
            <input className="input" type="text" name="job_title" value={values.job_title} onChange={(e) => setValues({ ...values, job_title: e.target.value })} />
          </Field>
          <Field label="Seniority" hint="Years of experience or level">
            <input className="input" type="text" name="seniority" value={values.seniority} onChange={(e) => setValues({ ...values, seniority: e.target.value })} />
          </Field>
        </div>

        <div className="border-t border-gray-100 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Field label="Role" hint="Access level in the app">
              <select className="input" name="role" value={values.role} onChange={(e) => setValues({ ...values, role: e.target.value })}>
                <option value="super_admin" disabled>
                  Super Admin
                </option>
                <option value="admin">Admin</option>
                <option value="user">User</option>
                <option value="applicant">Applicant</option>
                <option value="client">Client</option>
              </select>
            </Field>
            <Field label="Billability" hint="Non-billable people are excluded from TACE and profitability by default">
              <label className="flex items-center gap-2 cursor-pointer select-none mt-1">
                <input
                  type="checkbox"
                  checked={!!values.non_billable}
                  onChange={(e) => setValues({ ...values, non_billable: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Non-billable</span>
              </label>
            </Field>
          </div>
        </div>
      </div>
    </SectionCard>
  );
};

// ─── 3. Organisation & Location ─────────────────────────────────────────────

const OrganisationDetails = ({ values, setValues, businessUnits = [] }) => {
  const [organisations, setOrganisations] = useState([]);
  const [notationTemplates, setNotationTemplates] = useState([]);

  useEffect(() => {
    (async () => {
      const { data, ok } = await api.post("/organisation/search", { slug_only: true });
      if (ok && data) setOrganisations(data);
    })();
  }, []);

  // Only offer notation templates that belong to this person's business unit
  useEffect(() => {
    if (!values.business_unit_id) {
      setNotationTemplates([]);
      return;
    }
    (async () => {
      const { data, ok } = await api.post("/notation_template/search", { business_unit_id: values.business_unit_id });
      if (ok && data) setNotationTemplates(data);
    })();
  }, [values.business_unit_id]);

  return (
    <SectionCard title="Organisation & Location" description="Where this person sits legally, operationally and physically">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Field label="Organisation" hint="The legal company this person is employed/contracted by. Determines invoicing details, IBAN, VAT number and country-specific rules (e.g. Selego BV = NL, Le Collectif = FR).">
          <select
            className="input"
            name="organisation_id"
            value={values.organisation_id || ""}
            onChange={(e) => {
              const org = organisations.find((o) => o._id === e.target.value);
              setValues({ ...values, organisation_id: org?._id || "", organisation_name: org?.name || "" });
            }}>
            <option value=""></option>
            {organisations.map((org) => (
              <option key={org._id} value={org._id}>
                {org.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Business Unit" hint="The internal team this person belongs to. Used for filtering dashboards, TACE, profitability and team occupancy reports.">
          <select
            className="input"
            name="business_unit_id"
            value={values.business_unit_id || ""}
            onChange={(e) => {
              const bu = businessUnits.find((b) => b._id === e.target.value);
              setValues({ ...values, business_unit_id: e.target.value, business_unit_name: bu?.name || "" });
            }}>
            <option value="">Not set</option>
            {businessUnits.map((bu) => (
              <option key={bu._id} value={bu._id}>
                {bu.emoji} {bu.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Office" hint="Where this person physically works day-to-day. Independent from their legal entity or team.">
          <select className="input" name="office" value={values.office} onChange={(e) => setValues({ ...values, office: e.target.value })}>
            <option value=""></option>
            <option value="PARIS">PARIS</option>
            <option value="AMSTERDAM">AMSTERDAM</option>
            <option value="BARCELONE">BARCELONE</option>
            <option value="TIRANA">TIRANA</option>
            <option value="REMOTE">REMOTE</option>
          </select>
        </Field>
        <Field label="Notation Template" hint="Performance review template for this person's business unit">
          <select
            className="input"
            name="notation_template_id"
            value={values.notation_template_id || ""}
            onChange={(e) => setValues({ ...values, notation_template_id: e.target.value })}>
            <option value="">Not set</option>
            {notationTemplates.map((t) => (
              <option key={t._id} value={t._id}>{t.title}</option>
            ))}
          </select>
        </Field>
        <Field label="Manager" hint="Direct manager — used for team hierarchy and managee filters">
          <div className="flex gap-2">
            <SelectUser
              value={{ _id: values.manager_id, name: values.manager_name, avatar: values.manager_avatar }}
              userName={values.manager_name}
              onChange={(e) => setValues({ ...values, manager_id: e?._id, manager_name: e?.name, manager_avatar: e?.avatar })}
            />
            {values.manager_id && (
              <button
                type="button"
                className="text-red-400 hover:text-red-600 transition-colors"
                onClick={async () => {
                  if (!confirm(`Are you sure to remove ${values.manager_name} as ${values.name}'s manager?`)) return;
                  const { data } = await api.put(`/user/${values._id}`, { manager_id: null, manager_name: null, manager_avatar: null });
                  setValues(data);
                  toast.success("Updated!");
                }}>
                ✕
              </button>
            )}
          </div>
        </Field>
      </div>
    </SectionCard>
  );
};

// ─── 4. Dates & Eligibility ─────────────────────────────────────────────────

const DatesDetails = ({ values, setValues }) => {
  return (
    <SectionCard title="Dates & Eligibility" description="Employment timeline and tax credit eligibility">
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Starting date">
            <input
              className="input"
              type="date"
              name="starting_date"
              value={values.starting_date ? new Date(values.starting_date).toISOString().split("T")[0] : null}
              onChange={(e) => setValues({ ...values, starting_date: e.target.value })}
            />
          </Field>
          {values?.contract === "CDI" && (
            <Field label="End testing date" hint="End of trial period (CDI only)">
              <input
                className="input"
                type="date"
                name="end_test_date"
                value={values.end_test_date ? new Date(values.end_test_date).toISOString().split("T")[0] : null}
                onChange={(e) => setValues({ ...values, end_test_date: e.target.value })}
              />
            </Field>
          )}
          <Field label="Exit date">
            <input
              className="input"
              type="date"
              name="exit_date"
              value={values.exit_date ? new Date(values.exit_date).toISOString().split("T")[0] : null}
              onChange={(e) => setValues({ ...values, exit_date: e.target.value })}
            />
          </Field>
          <Field label="Remaining holidays" hint="Days of leave remaining">
            <input
              className="input"
              type="number"
              name="remaining_holidays"
              value={values.remaining_holidays}
              onChange={(e) => setValues({ ...values, remaining_holidays: e.target.value })}
            />
          </Field>
        </div>

        <div className="border-t border-gray-100 pt-4">
          <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-2">Tax credit eligibility</p>
          <div className="flex items-center gap-8">
            <label htmlFor="wbso_eligible" className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                id="wbso_eligible"
                checked={values.wbso_eligible}
                onChange={(e) => setValues({ ...values, wbso_eligible: e.target.checked })}
                className="rounded"
              />
              <div>
                <span className="text-sm text-gray-700">WBSO</span>
                <span className="text-[10px] text-gray-400 ml-1">Netherlands R&D</span>
              </div>
            </label>
            <label htmlFor="cir_eligible" className="flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" id="cir_eligible" checked={values.cir_eligible} onChange={(e) => setValues({ ...values, cir_eligible: e.target.checked })} className="rounded" />
              <div>
                <span className="text-sm text-gray-700">CIR</span>
                <span className="text-[10px] text-gray-400 ml-1">France R&D</span>
              </div>
            </label>
          </div>
        </div>
      </div>
    </SectionCard>
  );
};

// ─── 5. Admin & Payment ─────────────────────────────────────────────────────

const AdminDetails = ({ values, setValues }) => {
  const validateCompanyVat = (e) => {
    const vatNumber = e.target.value;
    const countryCode = values.company_country || "FR";

    const vatPrefix = vatNumber.slice(0, 2).toUpperCase();
    const isVatLongEnough = vatNumber.length >= 2;
    const isCountryValid = countryCode !== "OTHER";
    const doesPrefixMatchCountry = vatPrefix === countryCode;

    if (isVatLongEnough && isCountryValid && !doesPrefixMatchCountry) {
      toast.error(`VAT number should start with country code ${countryCode}`);
      return;
    }

    setValues({ ...values, company_vat: vatNumber });
  };

  return (
    <SectionCard title="Admin & Payment" description="Payment, banking and company details for freelancers">
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Status" hint="Active or deactivated user">
            <select className="input" name="status" value={values.status} onChange={(e) => setValues({ ...values, status: e.target.value })}>
              <option value=""></option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </Field>
          <Field label="Payment method">
            <select className="input" name="payment_method" value={values.payment_method} onChange={(e) => setValues({ ...values, payment_method: e.target.value })}>
              <option value=""></option>
              {Object.keys(PAYMENT_METHODS).map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
            </select>
          </Field>
          <Field label="IBAN">
            <input className="input" type="text" name="iban" value={values.iban} onChange={(e) => setValues({ ...values, iban: e.target.value })} />
          </Field>
          <Field label="BSN" hint="Dutch social security number">
            <input className="input" type="text" name="bsn" value={values.bsn} onChange={(e) => setValues({ ...values, bsn: e.target.value })} />
          </Field>
        </div>

        {values.contract === "FREELANCE" && (
          <div className="border-t border-gray-100 pt-4">
            <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-2">Freelancer company details</p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <Field label="Company Name">
                <input className="input" type="text" name="company_name" value={values.company_name} onChange={(e) => setValues({ ...values, company_name: e.target.value })} />
              </Field>
              <Field label="Company Address">
                <input
                  className="input"
                  type="text"
                  name="company_address"
                  value={values.company_address}
                  onChange={(e) => setValues({ ...values, company_address: e.target.value })}
                />
              </Field>
              <Field label="Company Country">
                <select
                  className="input"
                  name="company_country"
                  value={values.company_country}
                  onChange={(e) => setValues({ ...values, company_country: e.target.value, company_vat: "" })}>
                  <option value=""> </option>
                  <option value="FR">France</option>
                  <option value="NL">Netherlands</option>
                  <option value="ES">Spain</option>
                  <option value="OTHER">Other</option>
                </select>
              </Field>
              <Field label="Registration Number (SIREN)">
                <input className="input" type="text" name="company_number" value={values.company_number} onChange={(e) => setValues({ ...values, company_number: e.target.value })} />
              </Field>
              <Field label="Company VAT">
                <input autoComplete="none" className="input" type="text" name="company_vat" value={values.company_vat} onChange={validateCompanyVat} />
              </Field>
            </div>
          </div>
        )}
      </div>
    </SectionCard>
  );
};

export default About;
